import React, { useState, useEffect } from 'react';
import { AutoBillingConfig, AutoBillingService } from '../services/autoBillingService';
import { 
  ClockIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';

interface AutoBillingStatusProps {
  onProcessBilling: () => void;
}

export const AutoBillingStatus: React.FC<AutoBillingStatusProps> = ({ onProcessBilling }) => {
  const [configs, setConfigs] = useState<AutoBillingConfig[]>([]);
  const [dueConfigs, setDueConfigs] = useState<AutoBillingConfig[]>([]);
  const [nextBillingDate, setNextBillingDate] = useState<Date | null>(null);

  useEffect(() => {
    loadStatus();
    // Verificar cada hora
    const interval = setInterval(loadStatus, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadStatus = () => {
    const allConfigs = AutoBillingService.getAutoBillingConfigs();
    setConfigs(allConfigs);
    
    const due = AutoBillingService.checkDailyBilling();
    setDueConfigs(due);
    
    // Encontrar la próxima fecha de facturación
    const activeConfigs = allConfigs.filter(c => c.isActive);
    if (activeConfigs.length > 0) {
      const nextDates = activeConfigs.map(c => new Date(c.nextBillingDate));
      const nextDate = new Date(Math.min(...nextDates.map(d => d.getTime())));
      setNextBillingDate(nextDate);
    }
  };

  const getStatusColor = (config: AutoBillingConfig) => {
    const nextDate = new Date(config.nextBillingDate);
    const today = new Date();
    const daysUntil = Math.ceil((nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntil <= 0) return 'text-red-600';
    if (daysUntil <= 3) return 'text-orange-600';
    if (daysUntil <= 7) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusIcon = (config: AutoBillingConfig) => {
    const nextDate = new Date(config.nextBillingDate);
    const today = new Date();
    const daysUntil = Math.ceil((nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntil <= 0) return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
    if (daysUntil <= 3) return <ClockIcon className="h-5 w-5 text-orange-600" />;
    if (daysUntil <= 7) return <CalendarDaysIcon className="h-5 w-5 text-yellow-600" />;
    return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
  };

  if (configs.length === 0) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <CalendarDaysIcon className="h-6 w-6 text-blue-600 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Facturación Automática</h3>
            <p className="text-sm text-blue-700">No hay configuraciones activas. Configura tu primera facturación automática.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Estado General */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">Estado de Facturación Automática</h3>
          {dueConfigs.length > 0 && (
            <button
              onClick={onProcessBilling}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 text-sm"
            >
              Procesar {dueConfigs.length} factura{dueConfigs.length > 1 ? 's' : ''} pendiente{dueConfigs.length > 1 ? 's' : ''}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{configs.length}</div>
            <div className="text-sm text-gray-600">Configuraciones</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{configs.filter(c => c.isActive).length}</div>
            <div className="text-sm text-gray-600">Activas</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{dueConfigs.length}</div>
            <div className="text-sm text-gray-600">Pendientes</div>
          </div>
        </div>
      </div>

      {/* Próximas Facturaciones */}
      {nextBillingDate && (
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">Próxima Facturación</h4>
          <div className="flex items-center space-x-3">
            <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
            <div>
              <span className="text-lg font-semibold text-gray-900">
                {nextBillingDate.toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
              <div className="text-sm text-gray-600">
                {Math.ceil((nextBillingDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} días restantes
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Configuraciones */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-4 py-3 border-b">
          <h4 className="text-md font-medium text-gray-900">Configuraciones de Facturación</h4>
        </div>
        <div className="divide-y">
          {configs.map(config => (
            <div key={config.id} className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(config)}
                  <div>
                    <div className="font-medium text-gray-900">{config.clientName}</div>
                    <div className="text-sm text-gray-600">{config.domain}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-medium ${getStatusColor(config)}`}>
                    {new Date(config.nextBillingDate).toLocaleDateString('es-ES')}
                  </div>
                  <div className="text-sm text-gray-600">
                    {config.monthlyAmount}€/mes
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Alertas */}
      {dueConfigs.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Facturas Pendientes</h3>
              <p className="text-sm text-red-700">
                Tienes {dueConfigs.length} factura{dueConfigs.length > 1 ? 's' : ''} pendiente{dueConfigs.length > 1 ? 's' : ''} de generar y enviar.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 